import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useBlocker,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router';
import { eventCode, eventName, eventPlace, eventProblems } from '@wr/shared';
import { ApiError, errorMessage, get, post, put } from '../../api.js';
import { useToast } from '../../components/Toast.js';
import {
  Dialog,
  EmptyState,
  Field,
  ManyOfChips,
  Notice,
  PageHead,
  SelectField,
} from '../../components/ui.js';
import { NAV } from '../../nav.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import { KioskContentWindow } from '../kiosk/KioskScreen.js';
import { PrepareOffline } from '../content/PrepareOffline.js';
import { WrapUpWindow } from '../recap/RecapScreen.js';
import { capturesText, listEvents, type EventRow } from './data.js';
import { candidateLabel, type TeamCandidate } from '../travel/data.js';

interface EventsContext {
  events: EventRow[] | null;
  offline: boolean;
  /** Reads the list again (a change made on the portal, not on this device). */
  reload(): void;
}

/**
 * Events (brief section 7.1, docs/prototype.html), mounted at /events/*:
 * Active and Completed, each card with its event ID and its numbers.
 * Its Contacts/Leads button shows them filtered on it, without changing
 * the current event, and Event recap opens its recap (live, or frozen 30
 * days after its wrap-up); the card itself opens nothing (round 2). New and Edit are for admins (the brief wins over the
 * prototype, which shows Edit to everyone).
 */
export function EventsScreen() {
  return (
    <Routes>
      <Route element={<EventList />}>
        <Route index element={null} />
        <Route path="new" element={<EventWindow />} />
        <Route path=":id/edit" element={<EventWindow />} />
        <Route path=":id/wrap-up" element={<WrapUpWindow back="events" />} />
        <Route path=":id/reopen" element={<ReopenWindow />} />
        <Route path=":id/kiosk-content" element={<KioskContent />} />
      </Route>
    </Routes>
  );
}

function EventList() {
  const { can, refresh } = useSignedIn();
  const { store, counts, eventId } = useOffline();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [version, setVersion] = useState(0);
  const manage = can('events.manage');

  useEffect(() => {
    let current = true;
    listEvents(store)
      .then((list) => {
        if (!current) return;
        setError(null);
        setEvents(list.items);
        setOffline(list.offline);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
    // counts: a change saved or received refreshes the list.
  }, [store, counts, refresh, version]);

  const shown = useMemo(() => {
    const words = q.trim().toLowerCase();
    if (!events || !words) return events;
    return events.filter((e) =>
      [eventName(e), e.event_code, e.location]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(words),
    );
  }, [events, q]);

  const sections = [
    { title: 'Active', items: shown?.filter((e) => e.status !== 'completed') ?? [] },
    { title: 'Completed', items: shown?.filter((e) => e.status === 'completed') ?? [] },
  ];

  return (
    <>
      <PageHead
        title="Events"
        actions={
          manage && (
            <button type="button" className="btn sm primary" onClick={() => navigate('new')}>
              New event
            </button>
          )
        }
        lede="Trade shows. Pick one as the current event: every contact and lead captured is tagged with it, and with its ID in HubSpot."
      />
      <div className="toolbar">
        <input
          className="grow"
          type="search"
          placeholder="Search events"
          aria-label="Search events"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {offline && (
        <Notice>
          You are offline: the events this device keeps, without their numbers of contacts and
          leads.
        </Notice>
      )}
      {!events && !error && <div className="spinner" aria-label="Loading" />}
      {events && events.length === 0 && (
        <EmptyState title="No event yet.">
          {manage ? 'Create the first one with New event.' : 'An administrator creates them.'}
        </EmptyState>
      )}
      {events && events.length > 0 && shown?.length === 0 && (
        <EmptyState title="No event matches this search." />
      )}
      {sections.map(
        (section) =>
          section.items.length > 0 && (
            <section key={section.title} className="ev-sec" aria-label={section.title}>
              <div className="lp-h">{section.title}</div>
              {section.items.map((e) => (
                <EventCard key={e.id} event={e} current={e.id === eventId} manage={manage} />
              ))}
            </section>
          ),
      )}
      <Outlet
        context={
          { events, offline, reload: () => setVersion((v) => v + 1) } satisfies EventsContext
        }
      />
    </>
  );
}

function EventCard({
  event: e,
  current,
  manage,
}: {
  event: EventRow;
  current: boolean;
  manage: boolean;
}) {
  const navigate = useNavigate();
  const { can } = useSignedIn();
  const wrapUp = can('events.wrapup');
  const completed = e.status === 'completed';
  const name = eventName(e);
  const kioskItems = Array.isArray(e.kiosk_asset_ids) ? e.kiosk_asset_ids.length : 0;
  return (
    <div className={`evc${current ? ' cur' : ''}`}>
      <div className="evc-main">
        <span className="evc-top">
          <b>{name}</b>
          <span className={`badge ${completed ? 'b-off' : 'b-ok'}`}>
            {completed ? 'Completed' : 'Active'}
          </span>
          {current && <span className="badge b-hot">Current</span>}
        </span>
        {eventPlace(e) && <span className="evc-sub">{eventPlace(e)}</span>}
        <span className="evc-sub">
          <code>{String(e.event_code ?? eventCode(String(e.series), Number(e.year)))}</code>
        </span>
        {e.contacts !== undefined && (
          <span className="evc-stats">
            <span>
              <b>{e.contacts}</b> Contacts
            </span>
            <span>
              <b>{e.leads ?? 0}</b> Leads
            </span>
          </span>
        )}
        {/* Round 2: before the show, this device's offline content. */}
        <PrepareOffline event={e} />
      </div>
      <span className="actions">
        {can('kiosk.start') && !completed && (
          <button
            type="button"
            className="btn sm primary"
            aria-label={`Start booth kiosk for ${name}`}
            disabled={kioskItems === 0}
            title={kioskItems === 0 ? 'Choose its booth kiosk content first' : undefined}
            onClick={() => navigate(`/kiosk/${e.id}`)}
          >
            Start booth kiosk
          </button>
        )}
        {can('kiosk.content.manage') && !completed && (
          <button
            type="button"
            className="btn sm"
            aria-label={`Booth kiosk content for ${name} (${kioskItems})`}
            onClick={() => navigate(`${e.id}/kiosk-content`)}
          >
            Booth kiosk content ({kioskItems})
          </button>
        )}
        {manage && !completed && (
          <button
            type="button"
            className="btn sm"
            aria-label={`Edit ${name}`}
            onClick={() => navigate(`${e.id}/edit`)}
          >
            Edit
          </button>
        )}
        {wrapUp && !completed && (
          <button
            type="button"
            className="btn sm"
            aria-label={`Wrap up ${name}`}
            onClick={() => navigate(`${e.id}/wrap-up`)}
          >
            Wrap up
          </button>
        )}
        {wrapUp && completed && (
          <button
            type="button"
            className="btn sm"
            aria-label={`Reopen ${name}`}
            onClick={() => navigate(`${e.id}/reopen`)}
          >
            Reopen
          </button>
        )}
        {NAV.people.allowed(can) && (
          <button
            type="button"
            className="btn sm"
            aria-label={`Contacts/Leads of ${name}`}
            onClick={() => navigate(`${NAV.people.path}?event=${encodeURIComponent(e.id)}`)}
          >
            {NAV.people.label}
          </button>
        )}
        {NAV.recap.allowed(can) && (
          <button
            type="button"
            className="btn sm"
            aria-label={`Event recap of ${name}`}
            onClick={() => navigate(`${NAV.recap.path}/${encodeURIComponent(e.id)}`)}
          >
            {NAV.recap.label}
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Reopen (admins, round 2): a wrapped up event goes back to Active, open to
 * capture again; its recap is computed again at the next wrap-up. Needs
 * the network.
 */
function ReopenWindow() {
  const { id = '' } = useParams();
  const { events, reload } = useOutletContext<EventsContext>();
  const { can } = useSignedIn();
  const { sendNow } = useOffline();
  const navigate = useNavigate();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const event = events?.find((e) => e.id === id);
  const name = event ? eventName(event) : 'this event';
  const close = () => navigate('/events', { replace: true });

  if (!can('events.wrapup')) {
    return (
      <Dialog small title="Not allowed" onClose={close}>
        <p className="note">Only an administrator reopens an event.</p>
      </Dialog>
    );
  }

  async function reopen() {
    setBusy(true);
    setError(null);
    try {
      await post(`/events/${encodeURIComponent(id)}/reopen`, {});
      await sendNow();
      reload();
      toast(`${name} reopened.`);
      close();
    } catch (err) {
      const message = err instanceof ApiError ? err.body.message : undefined;
      setError(typeof message === 'string' ? message : errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      small
      title={`Reopen ${name}?`}
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={() => void reopen()}
          >
            Reopen
          </button>
        </>
      }
    >
      <p className="dlg-text">
        It goes back to Active: the team can capture contacts and leads on it again. Its recap is
        computed again at the next wrap up.
      </p>
      {error && <Notice kind="error">{error}</Notice>}
    </Dialog>
  );
}

/** Booth kiosk content (admins): a window over the list, as a step of the history. */
function KioskContent() {
  const { id } = useParams();
  const { events, reload } = useOutletContext<EventsContext>();
  const navigate = useNavigate();
  const event = events?.find((e) => e.id === id);
  if (!events) return null;
  if (!event) return <Navigate to="/events" replace />;
  return (
    <KioskContentWindow
      event={event}
      onClose={(saved) => {
        if (saved) reload();
        navigate('/events', { replace: true });
      }}
    />
  );
}

interface Draft {
  series: string;
  year: string;
  location: string;
  starts_on: string;
  ends_on: string;
  status: string;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** New event or Edit event (admins): the event ID is built from the series and the year. */
function EventWindow() {
  const { id } = useParams();
  const { can } = useSignedIn();
  const { events } = useOutletContext<EventsContext>();
  const navigate = useNavigate();
  const close = () => navigate('/events', { replace: true });
  if (!can('events.manage')) {
    return (
      <Dialog small title="Not allowed" onClose={close}>
        <p className="note">Only an administrator creates and edits events.</p>
      </Dialog>
    );
  }
  if (!events) return null;
  const event = id ? events.find((e) => e.id === id) : undefined;
  if (id && !event) {
    return (
      <Dialog small title="Edit event" onClose={close}>
        <Notice kind="error">This event does not exist, or it was deleted.</Notice>
      </Dialog>
    );
  }
  return <EventForm key={id ?? 'new'} event={event} events={events} onClose={close} />;
}

function EventForm({
  event,
  events,
  onClose,
}: {
  event?: EventRow;
  events: EventRow[];
  onClose(): void;
}) {
  const { save } = useOffline();
  const toast = useToast();
  const formId = useId();
  const initial = useMemo<Draft>(
    () => ({
      series: str(event?.series),
      year: String(event?.year ?? new Date().getFullYear()),
      location: str(event?.location),
      starts_on: str(event?.starts_on).slice(0, 10),
      ends_on: str(event?.ends_on).slice(0, 10),
      status: str(event?.status) || 'active',
    }),
    [event],
  );
  const [draft, setDraft] = useState<Draft>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const done = useRef(false);
  const team = useTeamGoing(event);

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial) || team.changed;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !done.current && currentLocation.pathname !== nextLocation.pathname,
  );

  const code = draft.series.trim()
    ? eventCode(draft.series, Number(draft.year) || 0)
    : draft.year.trim();
  const seriesList = [...new Set(events.map((e) => str(e.series)).filter(Boolean))].sort();
  // An empty event, created by mistake, may be deleted; one with captures is completed instead.
  // Nov 2, 2026: Delete always shows and says why when it cannot (unknown offline).
  const captures = event?.captures;
  const captured = captures
    ? captures.contacts + captures.leads + captures.notes + captures.receipts
    : null;

  const set = (key: keyof Draft, value: string) => {
    setDraft({ ...draft, [key]: value });
    setErrors({ ...errors, [key]: undefined });
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problems: Partial<Record<keyof Draft, string>> = eventProblems(draft);
    if (!problems.series && events.some((x) => x.id !== event?.id && x.event_code === code)) {
      problems.series = `${code} already exists. Each edition is created once.`;
    }
    setErrors(problems);
    if (Object.keys(problems).length > 0) return;
    // The team needs the network: saved first, so nothing is half saved.
    if (!(await team.save())) return;
    const fields: Record<string, unknown> = {
      series: draft.series.trim(),
      year: Number(draft.year),
      location: draft.location.trim() || null,
      starts_on: draft.starts_on || null,
      ends_on: draft.ends_on || null,
    };
    if (event) fields.status = draft.status;
    // Only what changed, so a colleague's change to another field is kept.
    const changed = event
      ? Object.fromEntries(
          Object.entries(fields).filter(
            ([k, v]) =>
              (v ?? '') !== (k === 'year' ? Number(initial.year) : initial[k as keyof Draft]),
          ),
        )
      : { ...fields, status: 'active' };
    done.current = true;
    if (Object.keys(changed).length > 0) {
      await save({
        opId: newId(),
        entity: 'event',
        id: event?.id ?? newId(),
        action: 'upsert',
        fields: changed,
        changedAt: new Date().toISOString(),
      });
    }
    toast(event ? `Event saved: ${code}` : `Event created: ${code}`);
    onClose();
  }

  async function remove() {
    if (!event) return;
    done.current = true;
    await save({
      opId: newId(),
      entity: 'event',
      id: event.id,
      action: 'delete',
      changedAt: new Date().toISOString(),
    });
    toast(`Event deleted: ${eventName(event)}`);
    onClose();
  }

  return (
    <Dialog
      title={event ? 'Edit event' : 'New event'}
      onClose={onClose}
      footer={
        <>
          {event && (
            <button
              type="button"
              className="btn danger left"
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary">
            {event ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <form id={formId} className="dlg-form" noValidate onSubmit={(e) => void submit(e)}>
        <div className="g2">
          <Field
            label="Event"
            mark
            list={`${formId}-series`}
            autoComplete="off"
            placeholder="e.g. Ecomondo"
            value={draft.series}
            error={errors.series}
            onChange={(e) => set('series', e.target.value)}
          />
          <datalist id={`${formId}-series`}>
            {seriesList.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <Field
            label="Year"
            mark
            type="number"
            min={2000}
            max={2100}
            inputMode="numeric"
            value={draft.year}
            error={errors.year}
            onChange={(e) => set('year', e.target.value)}
          />
        </div>
        <div className="dup plain" aria-live="polite">
          <b>
            Event ID in HubSpot: <code>{code}</code>
          </b>
          <span>
            Set on every contact and lead of this event. Filter in HubSpot on the ID for one
            edition, or on the event name for all editions.
          </span>
        </div>
        <Field
          label="Location"
          placeholder="City, Country"
          value={draft.location}
          onChange={(e) => set('location', e.target.value)}
        />
        <div className="g2">
          <Field
            label="Start date"
            type="date"
            value={draft.starts_on}
            onChange={(e) => set('starts_on', e.target.value)}
          />
          <Field
            label="End date"
            type="date"
            min={draft.starts_on || undefined}
            value={draft.ends_on}
            error={errors.ends_on}
            onChange={(e) => set('ends_on', e.target.value)}
          />
        </div>
        {event && (
          <SelectField
            label="Status"
            value={draft.status}
            onChange={(e) => set('status', e.target.value)}
          >
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </SelectField>
        )}
        {team.allowed && (
          <>
            {team.people && team.chosen ? (
              <ManyOfChips
                label="Team going"
                options={team.people.map((p) => ({ value: p.id, label: candidateLabel(p) }))}
                value={team.chosen}
                onChange={team.choose}
              />
            ) : (
              !team.error && <div className="spinner" aria-label="Loading the team" />
            )}
            {team.error && <Notice kind="error">{team.error}</Notice>}
          </>
        )}
      </form>
      {confirmDelete && event && (
        <Dialog
          small
          title={
            captured === 0 ? `Delete ${eventName(event)}?` : `${eventName(event)} cannot be deleted`
          }
          onClose={() => setConfirmDelete(false)}
          footer={
            captured === 0 ? (
              <>
                <button
                  type="button"
                  className="btn"
                  autoFocus
                  onClick={() => setConfirmDelete(false)}
                >
                  Keep it
                </button>
                <button type="button" className="btn primary" onClick={() => void remove()}>
                  Delete
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn"
                autoFocus
                onClick={() => setConfirmDelete(false)}
              >
                Close
              </button>
            )
          }
        >
          {captured === 0 ? (
            <p className="note">
              Nothing was captured for it. An event with captures is never deleted: it is completed.
            </p>
          ) : captures && captured ? (
            <p className="note">
              {captured} capture{captured === 1 ? ' is' : 's are'} linked to it:{' '}
              {capturesText(captures)}. An event with captures is never deleted: wrap it up instead,
              it is then completed. Deleted contacts and leads count until they leave the portal.
            </p>
          ) : (
            <p className="note">
              Deleting an event needs the network: the portal first checks that nothing was captured
              for it.
            </p>
          )}
        </Dialog>
      )}
      {blocker.state === 'blocked' && (
        <Dialog
          small
          title="Close without saving?"
          onClose={() => blocker.reset?.()}
          footer={
            <>
              <button type="button" className="btn" autoFocus onClick={() => blocker.reset?.()}>
                Keep editing
              </button>
              <button type="button" className="btn primary" onClick={() => blocker.proceed?.()}>
                Close without saving
              </button>
            </>
          }
        >
          <p className="note">Your changes to this event are not saved.</p>
        </Dialog>
      )}
    </Dialog>
  );
}

/**
 * Team going (round 2): who goes to the show, also set from Edit on the
 * event's card (admins of the show logistics; the same team as Travel's
 * Edit show logistics). It needs the network.
 */
function useTeamGoing(event?: EventRow) {
  const { can } = useSignedIn();
  const allowed = Boolean(event) && can('travel.logistics.manage');
  const id = event?.id;
  const [people, setPeople] = useState<TeamCandidate[] | null>(null);
  const [before, setBefore] = useState<string[] | null>(null);
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed || !id) return;
    let current = true;
    Promise.all([
      get<{ items: TeamCandidate[] }>('/travel/people'),
      get<{ team: { id: string; name: string }[] }>(`/travel/events/${id}`),
    ]).then(
      ([list, page]) => {
        if (!current) return;
        // Someone on the team who left the portal still shows, to be removed.
        const others = page.team.filter((m) => !list.items.some((p) => p.id === m.id));
        setPeople([...list.items, ...others]);
        setBefore(page.team.map((m) => m.id));
        setChosen(page.team.map((m) => m.id));
      },
      (err: unknown) =>
        current &&
        setError(
          err instanceof ApiError && err.code === 'offline'
            ? 'Team going needs the network. The other fields can be saved now.'
            : errorMessage(err),
        ),
    );
    return () => {
      current = false;
    };
  }, [allowed, id]);

  const changed =
    before !== null &&
    chosen !== null &&
    JSON.stringify([...before].sort()) !== JSON.stringify([...chosen].sort());

  return {
    allowed,
    people,
    chosen,
    error,
    changed,
    choose: (ids: string[]) => {
      setChosen(ids);
      setError(null);
    },
    /** Saves the team when it changed; false when it could not (the window stays open). */
    async save(): Promise<boolean> {
      if (!changed || !id) return true;
      try {
        await put(`/travel/events/${id}/team`, { team: chosen });
        setBefore(chosen);
        return true;
      } catch (err) {
        setError(
          err instanceof ApiError && err.code === 'offline'
            ? 'Team going needs the network: try again when you are online.'
            : errorMessage(err),
        );
        return false;
      }
    },
  };
}
